import { Component, computed, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { RepoService } from "../services/repo.service";
import { Nps } from "../models/nps";
import { DatePipe } from "@angular/common";
import { CdkTableModule } from "@angular/cdk/table";
import { environment } from "../../environments/environment.development";

@Component({
  selector: 'app-nps',
  imports: [
    DatePipe,
    CdkTableModule,
  ],
  templateUrl: './nps.component.html',
  styleUrl: './nps.component.scss',
  standalone: true
})
export class NpsComponent implements OnInit {
  repoService = inject(RepoService);
  npsEntries: WritableSignal<Nps[]> = signal([]);
  totalNps = signal(0);
  npsScore = signal(0);
  nps7Days = signal(0);
  nps7DaysCount = signal(0);
  nps30Days = signal(0);
  nps30DaysCount = signal(0);
  pageSize = 20;
  currentPage = signal(1);
  totalPages = computed(() => Math.ceil(this.totalNps() / this.pageSize));
  activeFilter = signal('All');

  otherFilters = ['Comment', 'iOS', 'Android', 'Account', 'Drop-in', 'Contact'];

  private pageCursors: any[] = [null]; // Cursors for the start of each page

  displayedColumns: string[] = ['date', 'rating', 'feedback', 'os', 'appVersion', 'subscription', 'canContact'];

  async ngOnInit() {
    this.applyFilter('All');
  }

  async refreshPeriodStats(filter: string) {
    const [res7, res30] = await Promise.all([
      this.repoService.calculateNpsScoreForPeriod(7, filter),
      this.repoService.calculateNpsScoreForPeriod(30, filter)
    ]);

    this.nps7Days.set(res7.score);
    this.nps7DaysCount.set(res7.count);
    this.nps30Days.set(res30.score);
    this.nps30DaysCount.set(res30.count);
  }

  async applyFilter(filter: string) {
    if (this.activeFilter() === filter && this.npsEntries().length > 0) return;
    this.activeFilter.set(filter);

    this.currentPage.set(1);
    this.pageCursors = [null];
    this.npsEntries.set([]); // Immediate feedback

    try {
      // Fetch stats and first page
      const [score, count] = await Promise.all([
        this.repoService.calculateNpsScore(filter),
        this.repoService.getNpsCount(filter),
        this.refreshPeriodStats(filter)
      ]);

      this.npsScore.set(score);
      this.totalNps.set(count);

      if (count > 0) {
        await this.loadPage(1);
      } else {
        console.log(`No entries found for filter: ${filter}`);
      }
    } catch (e: any) {
      console.error("CRITICAL: Error applying filter. If this is an index error, check the link below:", e);
      if (e.message?.includes('index')) {
        alert("Firestore Index Required: Please check the browser console for the link to create the necessary index for this filter.");
      }
    }
  }

  async loadPage(pageNumber: number) {
    const total = this.totalPages();
    if (pageNumber < 1 || (total > 0 && pageNumber > total)) {
      console.warn(`Invalid page number: ${pageNumber}, total pages: ${total}`);
      return;
    }

    try {
      const startAfterDoc = this.pageCursors[pageNumber - 1];
      const { entries, lastVisible } = await this.repoService.getNpsEntries(this.activeFilter(), startAfterDoc);

      this.npsEntries.set(entries);
      this.currentPage.set(pageNumber);

      if (pageNumber < this.totalPages() && this.pageCursors.length === pageNumber) {
        this.pageCursors.push(lastVisible);
      }
    } catch (e) {
      console.error(`Error loading page ${pageNumber}:`, e);
    }
  }

  async downloadCsv() {
    const entries = await this.repoService.getAllNpsEntries(this.activeFilter());
    const uidCounts = entries.reduce((acc, entry) => {
      acc[entry.uid] = (acc[entry.uid] || 0) + 1;
      return acc;
    }, {});

    const duplicateUids = Object.keys(uidCounts).filter(uid => uidCounts[uid] > 1);
    console.log('UIDs with more than one NPS document:', duplicateUids);

    const csvColumns = ['date', 'rating', 'feedback', 'os', 'appVersion', 'subscription', 'uid', 'submissionCount'];

    const escapeCsvCell = (cell: any) => {
      if (cell === null || cell === undefined) {
        return '';
      }
      const stringCell = String(cell);
      if (stringCell.includes(',') || stringCell.includes('"') || stringCell.includes('\n')) {
        return `"${stringCell.replace(/"/g, '""')}"`;
      }
      return stringCell;
    };

    const header = csvColumns.join(',');
    const csv = entries.map(row => {
      return csvColumns.map(col => escapeCsvCell(row[col])).join(',');
    }).join('\n');

    const blob = new Blob([header + '\n' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nps.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  nextPage() {
    this.loadPage(this.currentPage() + 1);
  }

  previousPage() {
    this.loadPage(this.currentPage() - 1);
  }

  getRatingColor(rating: number): string {
    // This creates a color scale from red (hue 0) to green (hue 120)

    const index = 10 - rating;

    let colors = [
      "#00AD7B",
      "#4AB981",
      "#9DCC63",
      "#E3C74E",
      "#F8A40C",
      "#F68E1F",
      "#F07C31",
      "#EA6C40",
      "#DF594A",
      "#D44752",
      "#C73856",
    ];
    return colors[index];
  }

  async copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }

  navigateToDynamics(contactId: string) {
    const baseUrl = environment.production
      ? 'https://freya.crm4.dynamics.com/main.aspx?forceUCI=1&pagetype=entityrecord&etn=contact&id='
      : 'https://merpreprod.crm4.dynamics.com/main.aspx?forceUCI=1&pagetype=entityrecord&etn=contact&id=';

    window.open(`${baseUrl}${contactId}`, '_blank');
  }
}
